import React, { useEffect, useRef } from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "./FontAwesome";
import useStyles from "./Button.styles";
import { isNullOrEmpty, themeOptionalColorString } from "../../utils";
import type { Theme } from "../../types/Theme";
import { shouldUseModernTheme } from "../../theme/navigationButtonThemeColors";
import useQuestion from "../../hooks/useQuestion";
import { LookupTableNavigation } from "../controls/KeyboardNavigation/LookupTableNavigation";
import { useKeyboardNavigationForLookupTable } from "../../hooks/useKeyboardNavigationForLookupTable";

interface Props {
    className?: string;
    disabled?: boolean;
    active?: boolean;
    hidden?: boolean;
    icon?: IconProp;
    /** When set, rendered instead of the Font Awesome `icon`. */
    customIcon?: React.ReactNode;
    iconPosition?: "start" | "end";
    iconOnHover?: boolean;
    size?: "small" | "large" | "full" | "flex" | number;
    text?: string;
    displayText?: boolean;
    displayIcon?: boolean;
    onClick(): void;
    imageUrl?: string;
    buttonType?: string;
    /**
     * When `Theme.UseModernTheme` is true: secondary or neutral (Quit-style) outline.
     * If omitted with no `fillAccent`, defaults to neutral outline. Ignored otherwise.
     */
    outlineAccent?: "secondary" | "neutral";
    /** When `Theme.UseModernTheme` is true: solid primary fill. Ignored otherwise. */
    fillAccent?: "primary";
    buttonId?: string;
    questionId?: string;
    onKeyDownEventHandler?(
        event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement | HTMLButtonElement>,
    ): void;
    buttonElementRef?: React.MutableRefObject<any>;
    onFocus?: React.FocusEventHandler<HTMLButtonElement>;
    onBlur?: React.FocusEventHandler<HTMLButtonElement>;
}

interface IconProps {
    active: boolean;
    disabled?: boolean;
    iconOnHover: boolean;
    icon?: IconProp;
    customIcon?: React.ReactNode;
    iconPosition?: "start" | "end";
    size?: "small" | "large" | "full" | "flex" | number;
    textAndIcon: any;
    imageUrl?: string;
}

const MODERN_BORDERLESS_ICON_BUTTON_TYPES = new Set([
    "CLEARSTARRATING",
    "FILEUPLOAD_ACTION",
    "SIGNATURE_CLEAR",
    "SIGNATURE_UNDO",
]);

function isModernBorderlessIconButtonType(buttonType?: string): boolean {
    return Boolean(buttonType && MODERN_BORDERLESS_ICON_BUTTON_TYPES.has(buttonType));
}

function getSizeClassName(classes, size) {
    if (typeof size === "number") {
        return "";
    }
    switch (size) {
        case "small":
            return classes.buttonSmall;
        case "full":
            return classes.buttonFull;
        case "flex":
            return classes.buttonFlex;
        default:
            return classes.buttonLarge;
    }
}

function ButtonIcon(props: IconProps) {
    const {
        size,
        active,
        disabled,
        icon,
        customIcon,
        iconPosition,
        textAndIcon,
        iconOnHover,
        imageUrl,
    } = props;

    const theme = useTheme();
    const classes = useStyles({ theme, size, iconPosition, textAndIcon, iconOnHover });

    return (
        <div
            className={
                !imageUrl &&
                classnames(
                    classes.buttonIconContainer,
                    textAndIcon && iconOnHover && classes.buttonIconAnimated,
                    textAndIcon && active && iconOnHover && classes.buttonIconAnimatedHover,
                )
            }
        >
            {!imageUrl && customIcon && (
                <span
                    className={classnames(
                        classes.buttonIcon,
                        disabled && classes.buttonIconDisabled,
                    )}
                >
                    {customIcon}
                </span>
            )}
            {!imageUrl && !customIcon && icon != null && (
                <FontAwesomeIcon
                    fixedWidth
                    className={classnames(
                        classes.buttonIcon,
                        disabled && classes.buttonIconDisabled,
                    )}
                    icon={icon}
                />
            )}
            {imageUrl && <img src={imageUrl} className={classes.pointer} />}
        </div>
    );
}

export default function Button(props: Props) {
    const {
        size,
        className,
        disabled,
        onClick,
        active,
        hidden,
        text,
        icon,
        customIcon,
        iconPosition,
        iconOnHover,
        displayText,
        displayIcon,
        imageUrl,
        buttonType,
        outlineAccent,
        fillAccent,
        buttonId,
        onKeyDownEventHandler,
        buttonElementRef,
        questionId,
        onFocus: onFocusFromProps,
        onBlur: onBlurFromProps,
        ...otherProps
    } = props;

    const renderText = displayText !== false && !isNullOrEmpty(text);
    const renderIcon = displayIcon !== false && (icon != null || customIcon != null);
    const textAndIcon = renderText && renderIcon;

    const position = iconPosition || "end";

    const theme = useTheme() as Theme;
    const useModernTheme = shouldUseModernTheme(theme);

    const isModernBorderlessIconButton =
        useModernTheme && isModernBorderlessIconButtonType(buttonType);
    const useSecondaryOutline =
        useModernTheme &&
        outlineAccent === "secondary" &&
        !imageUrl &&
        !isModernBorderlessIconButtonType(buttonType);
    /**
     * Modern neutral outline: explicit `outlineAccent="neutral"`, or both accents omitted so
     * plain `<Button />` gets `buttonNeutral` instead of legacy `classes.button`.
     */
    const useNeutralOutline =
        useModernTheme &&
        (outlineAccent === "neutral" || (!outlineAccent && !fillAccent)) &&
        !imageUrl &&
        !isModernBorderlessIconButtonType(buttonType);
    const usePrimaryFill = useModernTheme && fillAccent === "primary" && !imageUrl;

    const classes = useStyles({
        theme,
        iconPosition: position,
    });

    const [_active, setActive] = React.useState(false);

    const isActive = active || _active;
    const buttonReference = useRef(null);

    const question = useQuestion();

    const onSelect = () => {
        if (disabled) {
            return;
        }
        if (onClick) {
            onClick();
        }
    };

    const { elementReference, onKeyDown } =
        (buttonType === "ADD_NEW_ITEM" || buttonType === "LOOKOUT_SEARCH_BTN") &&
        useKeyboardNavigationForLookupTable(
            question.id,
            buttonId,
            { id: question.id + buttonId },
            question,
            LookupTableNavigation,
            onSelect,
            buttonType,
        );

    const refHandler = (el) => {
        buttonReference.current = el;
        if (buttonType === "ADD_NEW_ITEM" || buttonType === "LOOKOUT_SEARCH_BTN") {
            elementReference.current = el;
            if (elementReference) {
                elementReference.current = el;
            }
        }
        if (buttonElementRef) {
            buttonElementRef.current = el;
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", handleKeyPress);
        return function cleanup() {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, []);

    function handleKeyPress(event) {
        if (event.key !== "Enter") return;
        // Every Button instance registers its own document-level keydown listener,
        // so a single Enter fans out to all of them. Bail if another listener has
        // already handled this same event (it calls preventDefault after navigating)
        // to guarantee exactly one navigation click per Enter. (DEV-98341)
        if (event.defaultPrevented) return;
        const focusedElement = document.activeElement;
        const excludedClasses = ["select__content", "select__option", "button"];
        // Exclude anchor and text input elements (INPUT and TEXTAREA) from Enter navigation
        const isExcludedElement =
            focusedElement &&
            (excludedClasses.some((className) => focusedElement.classList.contains(className)) ||
                focusedElement.tagName === "A" ||
                focusedElement.tagName === "INPUT" ||
                focusedElement.tagName === "TEXTAREA");

        if (!isExcludedElement) {
            const navigationButtons = {
                next: document.querySelector(".navigation__next"),
                submit: document.querySelector(".navigation__submit"),
            };

            const buttonToClick = navigationButtons.next || navigationButtons.submit;
            if (buttonToClick instanceof HTMLElement) {
                buttonToClick.click();
                event.preventDefault();
            }
        }
    }

    return (
        <button
            type="button"
            ref={(el) => refHandler(el)}
            aria-label={text}
            onFocus={(e) => {
                setActive(true);
                onFocusFromProps?.(e);
            }}
            onBlur={(e) => {
                setActive(false);
                onBlurFromProps?.(e);
            }}
            onMouseOver={() => setActive(true)}
            onMouseOut={() => setActive(false)}
            onClick={onSelect}
            onKeyPress={(event) => {
                if (disabled) {
                    return;
                }
                if (
                    !isModernBorderlessIconButtonType(buttonType) &&
                    (event.key === " " || event.key === "Enter")
                ) {
                    if (onClick) {
                        onClick();
                    }
                    event.preventDefault();
                }
            }}
            onKeyDown={(event) => {
                // CLEARSTARRATING: forward arrow keys only so Tab stays native; do not
                // preventDefault here — KeyboardNavigation does when focus actually moves.
                if (
                    buttonType === "CLEARSTARRATING" &&
                    onKeyDownEventHandler &&
                    (event.key === "ArrowLeft" ||
                        event.key === "ArrowRight" ||
                        event.key === "ArrowUp" ||
                        event.key === "ArrowDown")
                ) {
                    onKeyDownEventHandler(event);
                } else if (buttonType === "ADD_NEW_ITEM" || buttonType === "LOOKOUT_SEARCH_BTN") {
                    onKeyDown && onKeyDown(event);
                }
            }}
            className={classnames(
                className,
                !imageUrl &&
                    !isModernBorderlessIconButton &&
                    (useSecondaryOutline
                        ? classes.buttonSecondary
                        : useNeutralOutline
                          ? classes.buttonNeutral
                          : usePrimaryFill
                            ? classes.buttonPrimary
                            : classes.button),
                !imageUrl && classes.buttonBorderNone,
                !imageUrl && getSizeClassName(classes, size),
                isActive &&
                    !disabled &&
                    !imageUrl &&
                    !isModernBorderlessIconButton &&
                    (useSecondaryOutline
                        ? classes.buttonSecondaryActive
                        : useNeutralOutline
                          ? classes.buttonNeutralActive
                          : usePrimaryFill
                            ? classes.buttonPrimaryActive
                            : classes.buttonActive),
                disabled && classes.buttonDisabled,
                hidden && classes.buttonHidden,
            )}
            {...otherProps}
        >
            {position === "start" && renderIcon && (
                <ButtonIcon
                    size={size}
                    icon={icon}
                    customIcon={customIcon}
                    iconPosition={position}
                    iconOnHover={iconOnHover}
                    active={isActive}
                    textAndIcon={textAndIcon}
                    disabled={disabled}
                    imageUrl={imageUrl}
                />
            )}
            {renderText && !imageUrl && (
                <div
                    className={classnames(
                        classes.buttonTextContainer,
                        textAndIcon && iconOnHover && classes.buttonTextAnimated,
                        textAndIcon && isActive && iconOnHover && classes.buttonTextAnimatedHover,
                    )}
                    aria-hidden="true"
                >
                    {text}
                </div>
            )}
            {position === "end" && renderIcon && (
                <ButtonIcon
                    size={size}
                    icon={icon}
                    customIcon={customIcon}
                    active={isActive}
                    iconPosition={position}
                    textAndIcon={textAndIcon}
                    disabled={disabled}
                    iconOnHover={iconOnHover}
                    imageUrl={imageUrl}
                />
            )}
        </button>
    );
}
